import ActionButton from "@/components/buttons/ActionButton";
import HiddenInput from "@/components/form/components/HiddenInput";
import AntIcon from "@/components/icons/AntIcon";
import { selectStandardProps } from "@/utils/componentUtils";
import { setErrorsToForm } from "@/utils/formUtils";
import { regexPatterns, validations } from "@/utils/validationUtils";
import { useIsMutating } from "@tanstack/react-query";
import { App as AntApp, Col, Divider, Form, Input, Modal, Row, Select } from "antd";
import { Fragment, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  PARTNER_QUERY_KEYS,
  useCreatePartnerConfigs,
  useDeletePartnerConfigBackgroundPicture,
  useDeletePartnerConfigs,
  useEnumeration,
  useUpdatePartnerConfigBackgroundPicture,
  useUpdatePartnerConfigs,
} from "../../../../common/api/queries";
import { ModalSizes, rowGutter } from "../../../../common/constants";
import { UUID } from "../../../../typings/global";
import { useIsCurrentAgentTypeFingo, useIsCurrentAgentTypePlus } from "../../../auth/hooks";
import InstitutionSelect from "../../../enumerations/components/form/InstitutionSelect";
import { EnumerationType } from "../../../enumerations/enums";
import { InstitutionEnum } from "../../../institution/enums";
import { InstitutionBase } from "../../../institution/types";
import { PartnerSsoSubType, PartnerSsoType, ssoTypeToInstitutionMap } from "../../enums";
import {
  CreatePartnerConfig,
  PartnerConfig,
  PartnerConfigBackgroundPicture,
  PartnerConfigsAndSsoProperties,
  UpdatePartnerConfig,
} from "../../types";
import { PartnerConfigBackgroundPictureInput } from "./PartnerConfigBackgroundPictureInput";

interface BackgroundPictureFormValue {
  backgroundPicture?: PartnerConfigBackgroundPicture | File;
}

interface BackgroundPictureChange extends BackgroundPictureFormValue {
  id: UUID;
}

type CreatePartnerConfigFormValues = CreatePartnerConfig & BackgroundPictureFormValue & { id?: undefined };
type UpdatePartnerConfigFormValues = UpdatePartnerConfig & BackgroundPictureFormValue;
type PartnerConfigFormValues = CreatePartnerConfigFormValues | UpdatePartnerConfigFormValues;

const getPartnerConfigsDiff = (serverConfigs: PartnerConfig[], formConfigs: PartnerConfigFormValues[]) => {
  const serverIds = new Set(serverConfigs.map((c) => c.id));

  const toCreate = formConfigs.filter(
    (config): config is CreatePartnerConfigFormValues => !("id" in config) || config.id === undefined,
  );

  const toUpdate = formConfigs.filter(
    (config): config is UpdatePartnerConfigFormValues =>
      "id" in config && config.id !== undefined && serverIds.has(config.id),
  );

  const formIds = new Set(
    formConfigs.filter((config): config is UpdatePartnerConfigFormValues => "id" in config).map((config) => config.id),
  );

  const toDelete: UUID[] = serverConfigs.filter((config) => !formIds.has(config.id)).map((config) => config.id);

  return { toCreate, toUpdate, toDelete };
};

const omitBackgroundPicture = <T extends BackgroundPictureFormValue>({ backgroundPicture, ...config }: T) => config;

// The backend returns created configs in request order, which is the only link back to the form rows they came from
const pairCreatedConfigsWithPictures = (
  createdConfigs: PartnerConfig[],
  formConfigs: BackgroundPictureFormValue[],
): BackgroundPictureChange[] =>
  createdConfigs.map((config, index) => ({ id: config.id, backgroundPicture: formConfigs[index]?.backgroundPicture }));

interface Props {
  configsAndSsoProps?: PartnerConfigsAndSsoProperties;
  open: boolean;
  configs?: PartnerConfig[];
  onFormCancel: () => void;
}

export const PartnerConfigForm = ({ configsAndSsoProps, open, configs, onFormCancel }: Props) => {
  const { t } = useTranslation();
  const { notification } = AntApp.useApp();

  const [form] = Form.useForm<{ configs: PartnerConfigFormValues[] }>();
  const formConfigs = Form.useWatch("configs", form);

  const institutionsEnums = useEnumeration(EnumerationType.INSTITUTIONS);
  const isCurrentAgentTypeFingo = useIsCurrentAgentTypeFingo();
  const isCurrentAgentTypePlus = useIsCurrentAgentTypePlus();

  const createPartnerConfig = useCreatePartnerConfigs();
  const updatePartnerConfig = useUpdatePartnerConfigs();
  const deletePartnerConfig = useDeletePartnerConfigs({ globalSpinner: false });
  const updateBackgroundPicture = useUpdatePartnerConfigBackgroundPicture();
  const deleteBackgroundPicture = useDeletePartnerConfigBackgroundPicture();
  // Pictures are saved in parallel and isPending follows only the latest call of a mutation, so count all of them
  const isUploadingBackgroundPictures =
    useIsMutating({ mutationKey: PARTNER_QUERY_KEYS.updatePartnerConfigBackgroundPicture() }) > 0;
  const isDeletingBackgroundPictures =
    useIsMutating({ mutationKey: PARTNER_QUERY_KEYS.deletePartnerConfigBackgroundPicture() }) > 0;

  const allowedSsoTypes = useMemo(() => {
    const ssoTypes = [PartnerSsoType.MONLY, PartnerSsoType.EUCS, PartnerSsoType.NSURE];

    if (isCurrentAgentTypeFingo) {
      ssoTypes.push(PartnerSsoType.FINGO);
    }

    if (isCurrentAgentTypePlus) {
      ssoTypes.push(PartnerSsoType.LUNA);
      ssoTypes.push(PartnerSsoType.VZDY_VIAC);
    }

    ssoTypeToInstitutionMap.forEach((institutionEnum, ssoType) => {
      const institution = institutionsEnums.find((i) => i.institutionEnum === institutionEnum);
      if ((institution && !institution.deactivated) || configs?.some((c) => c.ssoType === ssoType)) {
        ssoTypes.push(ssoType);
      }
    });

    return ssoTypes;
  }, [institutionsEnums, configs, isCurrentAgentTypeFingo, isCurrentAgentTypePlus]);

  useEffect(() => {
    if (open && configs) {
      form.setFieldsValue({
        configs: configs.map((config) => ({ ...config, institutionId: config.institution?.id })),
      });
    }
  }, [open, configs, form]);

  const handleSsoTypeChange = (index: number, ssoType: PartnerSsoType) => {
    const values = [...form.getFieldsValue().configs];

    const config = values[index];

    if (config && ssoType !== PartnerSsoType.FINGO) {
      values[index] = { ...config, ssoSubType: undefined };
    }

    if (ssoType) {
      switch (ssoType) {
        case PartnerSsoType.MONLY:
          form.setFieldsValue({
            configs: {
              ...values,
              [index]: {
                ...values[index],
                link: configsAndSsoProps?.monlySsoProperties?.baseUrl,
                institutionId: undefined,
              },
            },
          });
          break;
        case PartnerSsoType.ALLIANZ:
          form.setFieldsValue({
            configs: {
              ...values,
              [index]: {
                ...values[index],
                link: configsAndSsoProps?.allianzSsoProperties?.entryUrl,
                institutionId: institutionsEnums.find((i) => i.institutionEnum === InstitutionEnum.ALLIANZ)?.id,
              },
            },
          });
          break;
        case PartnerSsoType.AXA:
          form.setFieldsValue({
            configs: {
              ...values,
              [index]: {
                ...values[index],
                link: configsAndSsoProps?.uniqaStudioSsoProperties?.entryUrl,
                institutionId: institutionsEnums.find((i) => i.institutionEnum === InstitutionEnum.AXA_NON_LIFE)?.id,
              },
            },
          });
          break;
        case PartnerSsoType.CSOB:
          form.setFieldsValue({
            configs: {
              ...values,
              [index]: {
                ...values[index],
                link: configsAndSsoProps?.csobSsoProperties?.entryUrl,
                institutionId: institutionsEnums.find((i) => i.institutionEnum === InstitutionEnum.CSOB)?.id,
              },
            },
          });
          break;
        case PartnerSsoType.EUCS:
          form.setFieldsValue({
            configs: {
              ...values,
              [index]: {
                ...values[index],
                link: configsAndSsoProps?.eucsSsoProperties?.entryUrl,
                institutionId: undefined,
              },
            },
          });
          break;
        case PartnerSsoType.FINGO:
          form.setFieldsValue({
            configs: {
              ...values,
              [index]: {
                ...values[index],
                link: configsAndSsoProps?.fingoSsoProperties?.entryUrl,
                institutionId: undefined,
              },
            },
          });
          break;
        case PartnerSsoType.GENERALI:
          form.setFieldsValue({
            configs: {
              ...values,
              [index]: {
                ...values[index],
                link: configsAndSsoProps?.generaliSsoProperties?.entryUrl,
                institutionId: institutionsEnums.find((i) => i.institutionEnum === InstitutionEnum.GENERALI)?.id,
              },
            },
          });
          break;
        case PartnerSsoType.IAD:
          form.setFieldsValue({
            configs: {
              ...values,
              [index]: {
                ...values[index],
                link: configsAndSsoProps?.iadSsoProperties?.entryUrl,
                institutionId: institutionsEnums.find((i) => i.institutionEnum === InstitutionEnum.IAD)?.id,
              },
            },
          });
          break;
        case PartnerSsoType.LUNA:
          form.setFieldsValue({
            configs: {
              ...values,
              [index]: {
                ...values[index],
                link: configsAndSsoProps?.lunaSsoProperties?.entryUrl,
                institutionId: undefined,
              },
            },
          });
          break;
        case PartnerSsoType.METLIFE:
          form.setFieldsValue({
            configs: {
              ...values,
              [index]: {
                ...values[index],
                link: configsAndSsoProps?.metLifeSsoProperties?.baseUrl,
                institutionId: institutionsEnums.find((i) => i.institutionEnum === InstitutionEnum.MET_LIFE)?.id,
              },
            },
          });
          break;
        case PartnerSsoType.NN_EXPERT_APP:
        case PartnerSsoType.NN_WEB_AGENT_APP:
          form.setFieldsValue({
            configs: {
              ...values,
              [index]: {
                ...values[index],
                link:
                  ssoType === PartnerSsoType.NN_EXPERT_APP
                    ? configsAndSsoProps?.nnSsoProperties?.expertAppEntryUrl
                    : configsAndSsoProps?.nnSsoProperties?.webAgentAppEntryUrl,
                institutionId: institutionsEnums.find((i) => i.institutionEnum === InstitutionEnum.NN)?.id,
              },
            },
          });
          break;
        case PartnerSsoType.NSURE:
          form.setFieldsValue({
            configs: {
              ...values,
              [index]: {
                ...values[index],
                link: configsAndSsoProps?.nsureSsoProperties?.entryUrl,
                institutionId: undefined,
              },
            },
          });
          break;
        case PartnerSsoType.UNIQA:
          form.setFieldsValue({
            configs: {
              ...values,
              [index]: {
                ...values[index],
                link: configsAndSsoProps?.uniqaSsoProperties?.entryUrl,
                institutionId: institutionsEnums.find((i) => i.institutionEnum === InstitutionEnum.UNIQA)?.id,
              },
            },
          });
          break;
        case PartnerSsoType.VZDY_VIAC:
          form.setFieldsValue({
            configs: {
              ...values,
              [index]: {
                ...values[index],
                link: configsAndSsoProps?.vzdyViacSsoProperties?.entryUrl,
                institutionId: undefined,
              },
            },
          });
          break;
        case PartnerSsoType.YOUPLUS:
          form.setFieldsValue({
            configs: {
              ...values,
              [index]: {
                ...values[index],
                link: configsAndSsoProps?.youPlusSsoProperties?.entryUrl,
                institutionId: institutionsEnums.find((i) => i.institutionEnum === InstitutionEnum.YOUPLUS)?.id,
              },
            },
          });
          break;
      }
    } else {
      form.setFieldsValue({
        configs: {
          ...values,
          [index]: { ...values[index], link: undefined, institutionId: configs?.[0]?.institution?.id },
        },
      });
    }
  };

  const saveBackgroundPicture = ({ id, backgroundPicture }: BackgroundPictureChange) => {
    const isNewPictureSelected = backgroundPicture instanceof File;
    const isSavedPictureRemoved =
      !backgroundPicture && !!configs?.find((config) => config.id === id)?.backgroundPicture;

    if (isNewPictureSelected) {
      const formData = new FormData();
      formData.append("file", backgroundPicture);

      return updateBackgroundPicture.mutateAsync({ id, object: formData });
    }

    return isSavedPictureRemoved ? deleteBackgroundPicture.mutateAsync(id) : undefined;
  };

  const saveBackgroundPictures = (changes: BackgroundPictureChange[]) =>
    Promise.allSettled(changes.map(saveBackgroundPicture));

  const handleFormSubmit = () => {
    form.validateFields().then((values) => {
      if (!configs) {
        createPartnerConfig.mutate(
          { configs: values.configs.map(omitBackgroundPicture) },
          {
            onSuccess: async (createdConfigs) => {
              await saveBackgroundPictures(pairCreatedConfigsWithPictures(createdConfigs, values.configs));

              onFormCancel();
              notification.success({
                title: t("common.operationSuccess"),
                description: t("common.createOperationSuccess"),
              });
            },
            onError: (error) => {
              setErrorsToForm(form, "partnerConfig.attrs", error.violations);
            },
          },
        );
      } else {
        const { toCreate, toUpdate, toDelete } = getPartnerConfigsDiff(configs, values.configs);

        Promise.all([
          toCreate.length
            ? createPartnerConfig.mutateAsync(
                { configs: toCreate.map(omitBackgroundPicture) },
                {
                  onError: (error) => setErrorsToForm(form, "partnerConfig.attrs", error.violations),
                },
              )
            : [],
          toUpdate.length
            ? updatePartnerConfig.mutateAsync(
                { configs: toUpdate.map(omitBackgroundPicture) },
                {
                  onError: (error) => setErrorsToForm(form, "partnerConfig.attrs", error.violations),
                },
              )
            : undefined,
          toDelete.length
            ? deletePartnerConfig.mutateAsync(toDelete, {
                onError: (error) => setErrorsToForm(form, "partnerConfig.attrs", error.violations),
              })
            : undefined,
        ])
          .then(([createdConfigs]) =>
            saveBackgroundPictures([...toUpdate, ...pairCreatedConfigsWithPictures(createdConfigs, toCreate)]),
          )
          .then(() => {
            notification.success({
              title: t("common.operationSuccess"),
              description: t("common.updateOperationSuccess"),
            });
            onFormCancel();
          });
      }
    });
  };

  return (
    <Modal
      width={ModalSizes.MEDIUM}
      open={open}
      title={configs ? t("partnerConfig.titles.updateConfig") : t("partnerConfig.titles.createConfig")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={
        createPartnerConfig.isPending ||
        updatePartnerConfig.isPending ||
        deletePartnerConfig.isPending ||
        isUploadingBackgroundPictures ||
        isDeletingBackgroundPictures
      }
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="partnerConfigsForm">
        <Form.List name="configs" initialValue={[{}]}>
          {(fields, { add, remove }) => (
            <>
              {fields.map((field, index) => {
                const isFingoSsoType = formConfigs?.[field.name]?.ssoType === PartnerSsoType.FINGO;

                return (
                  <Fragment key={field.key}>
                    <Divider style={{ marginTop: 0 }} />
                    <div style={{ position: "relative", paddingRight: 50 }}>
                      <Row gutter={rowGutter}>
                        <HiddenInput name={[field.name, "id"]} />
                        <HiddenInput name={[field.name, "optimisticLockVersion"]} />

                        <Col span={8}>
                          <Form.Item
                            name={[field.name, "label"]}
                            label={t("partnerConfig.attrs.label")}
                            rules={[validations.notBlank, validations.size(1, 255)]}
                          >
                            <Input />
                          </Form.Item>
                        </Col>

                        <Col span={8}>
                          <Form.Item
                            name={[field.name, "ssoType"]}
                            label={t("partnerConfig.attrs.ssoType")}
                            rules={[validations.none]}
                          >
                            <Select
                              {...selectStandardProps}
                              allowClear
                              options={allowedSsoTypes.map((ssoType) => ({
                                value: ssoType,
                                label: t("partnerSso.enums.ssoType." + ssoType),
                              }))}
                              disabled={
                                configs?.[0]?.ssoType === PartnerSsoType.MONLY ||
                                configs?.[0]?.ssoType === PartnerSsoType.LUNA ||
                                configs?.[0]?.ssoType === PartnerSsoType.VZDY_VIAC ||
                                configs?.[0]?.ssoType === PartnerSsoType.EUCS ||
                                configs?.[0]?.ssoType === PartnerSsoType.FINGO ||
                                configs?.[0]?.ssoType === PartnerSsoType.NSURE
                              }
                              onChange={(ssoType) => handleSsoTypeChange(index, ssoType)}
                            />
                          </Form.Item>
                        </Col>

                        <Col span={8}>
                          <InstitutionSelect
                            formItemProps={{
                              name: [field.name, "institutionId"],
                              label: t("partnerConfig.attrs.institutionId"),
                              rules: [validations.none],
                            }}
                            selectProps={{
                              allowClear: true,
                              disabled:
                                !isFingoSsoType &&
                                (!!formConfigs?.[field.name]?.ssoType || !!configs?.[0]?.institution),
                            }}
                            optionsProps={{
                              groupByType: true,
                              selected: configs?.[index]?.institution
                                ? [configs[index]?.institution as InstitutionBase]
                                : [],
                            }}
                          />
                        </Col>
                      </Row>
                      <Row gutter={rowGutter}>
                        {isFingoSsoType && (
                          <Col span={8}>
                            <Form.Item
                              name={[field.name, "ssoSubType"]}
                              label={t("partnerConfig.attrs.ssoSubType")}
                              rules={[validations.none]}
                            >
                              <Select
                                {...selectStandardProps}
                                allowClear
                                options={Object.values(PartnerSsoSubType).map((ssoSubType) => ({
                                  value: ssoSubType,
                                  label: t("partnerSso.enums.ssoSubType." + ssoSubType),
                                }))}
                              />
                            </Form.Item>
                          </Col>
                        )}

                        <Col span={isFingoSsoType ? 16 : 24}>
                          <Form.Item
                            name={[field.name, "link"]}
                            label={t("partnerConfig.attrs.link")}
                            rules={[
                              validations.notBlank,
                              validations.size(1, 255),
                              validations.pattern(regexPatterns.webPageRegex),
                            ]}
                          >
                            <Input disabled={!!formConfigs?.[field.name]?.ssoType && !isFingoSsoType} />
                          </Form.Item>
                        </Col>
                      </Row>
                      <Row gutter={rowGutter}>
                        <Col span={24}>
                          <Form.Item
                            name={[field.name, "backgroundPicture"]}
                            label={t("partnerConfig.attrs.backgroundPicture")}
                            rules={[validations.none]}
                          >
                            <PartnerConfigBackgroundPictureInput configId={formConfigs?.[field.name]?.id} />
                          </Form.Item>
                        </Col>
                      </Row>
                      {fields.length > 1 && (
                        <AntIcon
                          type="delete"
                          color="red"
                          size="huge"
                          style={{ position: "absolute", top: 4, right: 0, cursor: "pointer" }}
                          onClick={() => remove(field.name)}
                        />
                      )}
                    </div>
                  </Fragment>
                );
              })}

              {configs?.[0]?.ssoType !== PartnerSsoType.MONLY &&
                configs?.[0]?.ssoType !== PartnerSsoType.LUNA &&
                configs?.[0]?.ssoType !== PartnerSsoType.VZDY_VIAC &&
                configs?.[0]?.ssoType !== PartnerSsoType.EUCS &&
                configs?.[0]?.ssoType !== PartnerSsoType.FINGO &&
                configs?.[0]?.ssoType !== PartnerSsoType.NSURE && (
                  <ActionButton
                    icon="plus"
                    className="margin-top-small"
                    onClick={() => add({ institutionId: configs?.[0]?.institution?.id })}
                  >
                    {t("common.add")}
                  </ActionButton>
                )}
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};
